import React, { useEffect, useRef, useCallback } from 'react';
import {
  ModelNodeDataForm,
  Point3D,
  ProjectDto,
  useAddNodeBetweenNodesMutation,
  AddNodeBetweenNodesForm
} from 'providers/api';
import {
  object,
  SchemaOf,
  string
} from 'yup';
import { i18n } from 'i18n';
import useForm from 'hooks/useForm';
import { Weaken } from 'utils';
import { Box, Button, Typography } from '@mui/material';
import { FormikActions } from 'components/Form';
import { invalidate } from '@react-three/fiber';
import { createNodeAndSpool, PREVIEW_SPOOL_COLOUR } from '../../utils/nodeCreation';
import { validateBetweenNodesRow } from '../../utils/validation';
import BetweenNodesTable from './BetweenNodesTable';
import { BetweenNodesRow } from '../../types';
import { Model } from '../../../hooks/useModelState';
import { PCNode } from '../../../MineModel/Nodes';
import PCSpool from '../../../MineModel/Spools/PCSpool';
import { useToolHandlers } from '../../../contexts/ToolHandlerContext';

interface BetweenNodesFormProps {
  model: Model;
  project: ProjectDto;
  closeDialog: () => void;
}

const BetweenNodesSchema: SchemaOf<any> = object().shape({
  nodeId: string().required(i18n._('Node ID is required')),
});

export const BetweenNodesForm: React.FC<BetweenNodesFormProps> = ({
  model,
  project,
  closeDialog,
}) => {
  const tools = useToolHandlers();
  const selectedSpoolRef = useRef<PCSpool | null>(null);
  const clickPointRef = useRef<{ x: number; y: number; z: number } | null>(null);
  const isSelectingSpoolRef = useRef<boolean>(false);
  const previewStateRef = useRef<{
    nodeId: string | null;
    spoolIds: string[];
    originalSpool: PCSpool | null;
  } | null>(null);
  const addNodeBetweenNodesMutation = useAddNodeBetweenNodesMutation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [isSelectingSpool, setIsSelectingSpool] = React.useState(false);
  const [previewState, setPreviewState] = React.useState<{
    nodeId: string | null;
    spoolIds: string[];
    originalSpool: PCSpool | null;
  } | null>(null);

  const removePreviewArtifacts = useCallback((m: Model) => {
    if (!previewState) return;

    if (previewState.spoolIds.length) {
      const toRemove = previewState.spoolIds
        .map((id) => m.spools[id])
        .filter(Boolean);
      if (toRemove.length) m.removeSpools(toRemove);
    }

    if (previewState.nodeId) {
      const node = m.nodes[previewState.nodeId];
      if (node) {
        const spools = m.findSpoolsByNodeId(Object.values(m.spools), node.id);
        if (spools.length) m.removeSpools(spools);
        m.removeNodes([node]);
      }
    }

    if (previewState.originalSpool) {
      if (!m.spools[previewState.originalSpool.spoolId]) {
        m.addSpool(previewState.originalSpool);
      }
    }

    selectedSpoolRef.current = null;
    clickPointRef.current = null;
    previewStateRef.current = null;
    setPreviewState(null);
  }, [previewState]);

  const { formik, helpers } = useForm<Weaken<BetweenNodesRow>>({
    mutation: addNodeBetweenNodesMutation,
    formikConfig: {
      initialValues: {
        nodeId: '',
      },
      onSubmit: async (values, { setSubmitting }) => {
        setSubmitting(true);
        setValidationError(null);

        if (!selectedSpoolRef.current || !clickPointRef.current) {
          setValidationError(i18n._('Please select a spool first.'));
          setSubmitting(false);
          return;
        }

        const spool = selectedSpoolRef.current;
        const previousNode = spool.pointA;
        const endNode = spool.pointB;
        const spoolPipeId = spool.pipe.pipeId;

        const point: Point3D = {
          x: clickPointRef.current.x,
          y: clickPointRef.current.y,
          z: clickPointRef.current.z,
        };

        const data: ModelNodeDataForm = {
          nodeId: values.nodeId || '',
          point,
          pipeId: spoolPipeId,
          pit: undefined,
          isStope: false,
          isPit: false,
          stopeData: undefined,
          level: undefined,
          area: undefined,
          isPlantNode: false,
          ruptureDiskPressure: 0,
          chokeHead: 0,
        };

        // Splitting an edge is one command: the server removes the original spool and adds the two
        // halves in a single save of the per-mine reticulation document.
        const formData: AddNodeBetweenNodesForm = {
          node: data,
          nodeAId: previousNode.id,
          nodeBId: endNode.id,
          spoolPipeId,
        };

        try {
          await addNodeBetweenNodesMutation.mutateAsync({
            data: formData,
            projectId: project.entityId,
          });

          previewStateRef.current = null;
          setPreviewState(null);
          selectedSpoolRef.current = null;
          clickPointRef.current = null;

          setSubmitting(false);
          closeDialog();
        } catch (error) {
          removePreviewArtifacts(model);
          setSubmitting(false);
          setValidationError(i18n._('Failed to add node. Please try again.'));
        }
      },
      validationSchema: BetweenNodesSchema,
    },
  });

  const { values, handleSubmit, setFieldValue, isSubmitting, resetForm } = formik;

  const handleSpoolSelect = useCallback((spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => {
    if (!isSelectingSpoolRef.current || !model || !spool.pointA || !spool.pointB) return;

    if (!clickPoint) {
      setValidationError(i18n._('Could not determine click position. Please try again.'));
      return;
    }

    selectedSpoolRef.current = spool;
    clickPointRef.current = clickPoint;

    const previousNode = spool.pointA;
    const endNode = spool.pointB;

    // Spool ids are opaque GUIDs, so the edge is found by its endpoints, never by a derived id.
    const originalSpool = model.findSpoolByPair(previousNode.id, endNode.id);
    if (originalSpool) {
      model.removeSpools([originalSpool]);
    }

    const nodeData = {
      nodeId: formik.values.nodeId || '',
      x: clickPoint.x,
      y: clickPoint.y,
      z: clickPoint.z,
      pipeType: spool.pipe.pipeId,
      previousNodeId: [previousNode.id],
      pitName: '',
      frictionLoopLength: undefined,
      area: '',
      level: '',
      endAtExistingNode: endNode.id,
    };

    const safeExistingNodes = Object.values(model.nodes) as PCNode[];
    const rowValidation = validateBetweenNodesRow(nodeData as BetweenNodesRow, safeExistingNodes);
    if (!rowValidation.isValid) {
      setValidationError(rowValidation.errors.join(', '));
      if (originalSpool) {
        model.addSpool(originalSpool);
      }
      return;
    }

    setValidationError(null);

    const { newNode, spools } = createNodeAndSpool(nodeData, model, undefined, PREVIEW_SPOOL_COLOUR);
    model.addNode(newNode);
    model.addSpools(spools);
    const addedIds = spools.map((sp) => sp.spoolId);

    const newPreviewState = {
      nodeId: newNode.id,
      spoolIds: addedIds,
      originalSpool: originalSpool || null,
    };
    previewStateRef.current = newPreviewState;
    setPreviewState(newPreviewState);

    setIsSelectingSpool(false);
    isSelectingSpoolRef.current = false;
    tools.setSpoolClickHandler(null);
    invalidate();
  }, [model, removePreviewArtifacts, formik]);

  const handleCancelSelection = useCallback(() => {
    setIsSelectingSpool(false);
    isSelectingSpoolRef.current = false;
    tools.setSpoolClickHandler(null);
    removePreviewArtifacts(model);
  }, [model, removePreviewArtifacts]);

  const handleStartSelection = useCallback(() => {
    if (isSelectingSpoolRef.current || previewState?.nodeId) {
      handleCancelSelection();
      return;
    }
    if (!values.nodeId || values.nodeId.trim() === '') {
      setValidationError(i18n._('Please enter a Node ID before selecting a spool.'));
      return;
    }
    setValidationError(null);
    removePreviewArtifacts(model);
    setIsSelectingSpool(true);
    isSelectingSpoolRef.current = true;
    tools.setSpoolClickHandler(handleSpoolSelect);
  }, [handleSpoolSelect, values.nodeId, handleCancelSelection, model, removePreviewArtifacts, previewState]);

  const handleReset = useCallback(() => {
    removePreviewArtifacts(model);
    resetForm();
  }, [model, removePreviewArtifacts, resetForm]);

  useEffect(() => () => {
    tools.setSpoolClickHandler(null);
    if (model && previewStateRef.current) {
      removePreviewArtifacts(model);
    }
    invalidate();
  }, [removePreviewArtifacts]);

  return (
    <form onSubmit={handleSubmit}>
      {validationError && (
        <div
          style={{
            color: 'red',
            backgroundColor: '#ffebee',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px',
            border: '1px solid #ef9a9a',
          }}
        >
          {validationError}
        </div>
      )}

      <Box
        sx={{
          overflow: 'auto',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Button
            variant={isSelectingSpool ? 'contained' : 'outlined'}
            fullWidth
            onClick={isSelectingSpool ? handleCancelSelection : handleStartSelection}
            color={isSelectingSpool ? 'error' : 'secondary'}
            disabled={!values.nodeId || values.nodeId.trim() === ''}
          >
            {isSelectingSpool
              ? i18n._('Cancel Selection')
              : i18n._('Click to Select Spool')}
          </Button>
          {(() => {
            if (!values.nodeId || values.nodeId.trim() === '') {
              return (
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                  {i18n._('Please enter a Node ID to enable spool selection')}
                </Typography>
              );
            }
            if (isSelectingSpool) {
              return (
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                  {i18n._('Click on a spool in the 3D view to place the node')}
                </Typography>
              );
            }
            return null;
          })()}
        </Box>

        <BetweenNodesTable
          betweenNodesData={values}
          hasError={helpers.hasError}
          getErrorHelpText={helpers.getErrorHelpText}
          handleBetweenNodesChange={setFieldValue}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <FormikActions
            formik={formik}
            mutation={addNodeBetweenNodesMutation}
            submitText={isSubmitting ? i18n._('Adding...') : i18n._('Add Nodes')}
            right={[
              [
                i18n._('Reset'),
                <Button
                  key="reset"
                  variant="contained"
                  color="secondary"
                  onClick={handleReset}
                  disabled={!formik.dirty || isSubmitting}
                >
                  {i18n._('Reset')}
                </Button>,
              ],
              'submit',
            ]}
            submitDisableOverride={!values.nodeId || values.nodeId.trim() === '' || !selectedSpoolRef.current || !clickPointRef.current}
          />
        </Box>
      </Box>
    </form>
  );
};

export default BetweenNodesForm;
